import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  getDateValue,
  renderNumberCell,
  renderPercentCell,
} from "utils/dataGrid";
import { formatDateHumanReadable, formatDateYYYYMMDD } from "utils/formatting";
import { formatDurationFromMins } from "utils/formatting/timeFormatters";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import { useError } from "../../../contexts/ErrorContext";
import { useHealthMetrics } from "../../../hooks/useFulfillmentsAnalytics";

interface PipelineHealthMetricsTableProps {
  dateStart?: string;
  dateEnd?: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "week_start_date", sort: "desc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
  aggregation: {
    model: {},
  },
};

const PipelineHealthMetricsTable: React.FC<PipelineHealthMetricsTableProps> = ({
  dateStart,
  dateEnd,
}) => {
  const { showError } = useError();

  const {
    data: healthMetricsData,
    error,
    isLoading,
  } = useHealthMetrics(dateStart, dateEnd);

  useEffect(() => {
    if (error) {
      showError(error?.message || `Failed to get health metrics`);
    }
  }, [error, showError]);

  const columns_def: GridColDef[] = [
    {
      field: "week_start_date",
      headerName: "Week Start Date",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: (params: GridRenderCellParams<Number>) => (
        <Link to={`/shipping/weekly/${formatDateYYYYMMDD(params.value)}`}>
          {formatDateHumanReadable(params.value)}
        </Link>
      ),
    },
    {
      field: "count_scheduled_deliveries",
      headerName: "Count Scheduled Deliveries",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "count_export_ftq_succeeded",
      headerName: "Count Export FTQ Success",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "pct_export_ftq_succeeded",
      headerName: "Pct Export FTQ Success",
      width: 200,
      renderCell: renderPercentCell,
    },
    {
      field: "avg_duration_minutes",
      headerName: "Avg Export Duration (Mins)",
      width: 220,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatDurationFromMins(params.value),
    },
    {
      field: "min_duration_minutes",
      headerName: "Min Export Duration (Mins)",
      width: 200,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatDurationFromMins(params.value),
    },
    {
      field: "max_duration_minutes",
      headerName: "Max Export Duration (Mins)",
      width: 200,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatDurationFromMins(params.value),
    },
    {
      field: "min_row_count",
      headerName: "Min Volume",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "max_row_count",
      headerName: "Max Volume",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "total_row_count",
      headerName: "Total Volume",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "count_fulfillments",
      headerName: "Count Fulfillments",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "count_connector_fulfillments",
      headerName: "Count Connector Fulfillments",
      width: 220,
      renderCell: renderNumberCell,
    },
    {
      field: "pct_connector_fulfillments",
      headerName: "Pct Connector Fulfillments",
      width: 220,
      renderCell: renderPercentCell,
    },
    {
      field: "count_connector_ftq_succeeded",
      headerName: "Count Connector FTQ Success",
      width: 220,
      renderCell: renderNumberCell,
    },
    {
      field: "pct_connector_ftq_succeeded",
      headerName: "Pct Connector FTQ Success",
      width: 220,
      renderCell: renderPercentCell,
    },
    {
      field: "count_customers",
      headerName: "Count Customers",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "count_connector_customers",
      headerName: "Count Connector Customers",
      width: 220,
      renderCell: renderNumberCell,
    },
    {
      field: "pct_connector_customers",
      headerName: "Pct Connector Customers",
      width: 220,
      renderCell: renderPercentCell,
    },
  ];

  return (
    <GaneshDataGrid
      loading={isLoading}
      data={healthMetricsData || []}
      columns={columns_def}
      gridId="shippingHealthMetricsGrid"
      defaultGridState={defaultGridState}
    />
  );
};
export default PipelineHealthMetricsTable;
